/*
  Product Sibling Swap — B2C
  Switches between sibling products (color / size swatches linked via custom.colors)
  without a full page reload.

  On a swatch click, the product template's sections are fetched for the sibling via the
  Section Rendering API (?sections=), the theme's section instances are torn down, the
  markup is replaced, the instances are re-created, and the URL is updated in place with
  history.replaceState (so Back leaves the product page instead of stepping through colors).

  Falls back to a normal navigation whenever the swap can't be done cleanly, e.g. the
  sibling uses a different product template (its section ids won't match), the request
  fails, or the theme JS isn't available.

  Swatch links opt in with the data-sibling-swap attribute, and need the theme's
  js-no-transition class so the page-transition click handler leaves them alone.
*/
(function () {
  if (window.__productSiblingSwapInit) return;
  window.__productSiblingSwapInit = true;

  var SECTION_PREFIX = 'shopify-section-';
  // Template sections that don't depend on the product and are left alone
  var SKIP_SECTIONS = /__collection-return$/;
  // Section Rendering API limit for the ?sections= parameter
  var MAX_SECTIONS = 5;

  var cache = {};
  var requestToken = 0;

  function templateSections() {
    return Array.prototype.filter.call(
      document.querySelectorAll('.shopify-section[id^="' + SECTION_PREFIX + 'template--"]'),
      function (el) { return !SKIP_SECTIONS.test(el.id); }
    );
  }

  function sectionIds(wrappers) {
    return wrappers.map(function (el) { return el.id.slice(SECTION_PREFIX.length); });
  }

  function fetchSections(url, ids) {
    var key = url + '|' + ids.join(',');
    if (!cache[key]) {
      var requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('sections', ids.join(','));
      cache[key] = fetch(requestUrl.toString(), { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Section request failed: ' + response.status);
          return response.json();
        })
        .catch(function (err) {
          delete cache[key];
          throw err;
        });
    }
    return cache[key];
  }

  function parseSection(html) {
    if (!html) return null;
    var tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.querySelector('.shopify-section');
  }

  // Scripts inserted via innerHTML are inert; recreate them so they run like on a page load
  function runScripts(scope) {
    scope.querySelectorAll('script').forEach(function (oldScript) {
      var type = oldScript.getAttribute('type');
      if (type && type !== 'text/javascript' && type !== 'module') return;
      var script = document.createElement('script');
      Array.prototype.forEach.call(oldScript.attributes, function (attr) {
        script.setAttribute(attr.name, attr.value);
      });
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  function sectionElements(wrapper) {
    return wrapper.querySelectorAll('[data-section-id][data-section-type]');
  }

  function teardown(wrapper) {
    sectionElements(wrapper).forEach(function (el) {
      theme.sections._onSectionUnload({ detail: { sectionId: el.getAttribute('data-section-id') } });
    });
  }

  function setup(wrapper) {
    sectionElements(wrapper).forEach(function (el) {
      if (theme.sections.constructors[el.getAttribute('data-section-type')]) {
        theme.sections._createInstance(el);
      }
    });
  }

  // Shopper Approved loads a per-variant widget script (keyed by window.sa_product) that
  // fills the review stars and review list. Re-load it for the new variant.
  function refreshShopperApproved(scope) {
    var loader = document.querySelector('script[src*="shopperapproved.com/widgets/"][src*="/product-widget/"]');
    var idInput = scope.querySelector('form[action*="/cart/add"] [name="id"]');
    if (!loader || !idInput || !idInput.value) return;

    var src = loader.src.replace(/\/product\/[^/]+\/product-widget\//, '/product/' + idInput.value + '/product-widget/');
    window.sa_product = String(idInput.value);
    if (window.saProductWidgetInterval) clearInterval(window.saProductWidgetInterval);

    var script = document.createElement('script');
    script.src = src;
    document.head.appendChild(script);
  }

  function productContainer(scope) {
    return (scope || document).querySelector('[data-section-type="product"]:not([data-modal="true"])');
  }

  function swap(url, link) {
    var wrappers = templateSections();
    var ids = sectionIds(wrappers);
    var oldProduct = productContainer();

    if (!ids.length || ids.length > MAX_SECTIONS || !oldProduct || !window.theme || !theme.sections) {
      window.location.assign(url);
      return;
    }

    var token = ++requestToken;
    var restoreFocus = document.activeElement === link;
    oldProduct.classList.add('is-sibling-loading');

    fetchSections(url, ids)
      .then(function (data) {
        if (token !== requestToken) return;

        var replacements = ids.map(function (id) { return parseSection(data[id]); });
        if (replacements.some(function (el) { return !el; })) {
          throw new Error('Sibling uses a different product template');
        }

        var oldTitle = oldProduct.getAttribute('data-product-title');
        var pageTitle = null;
        replacements.forEach(function (el) {
          var tpl = el.querySelector('template[data-sibling-page-title]');
          var titleEl = tpl && tpl.content.querySelector('title');
          var text = titleEl && titleEl.textContent.replace(/\s+/g, ' ').trim();
          if (text) pageTitle = text;
        });

        wrappers.forEach(function (wrapper, i) {
          var replacement = replacements[i];
          teardown(wrapper);
          wrapper.replaceWith(replacement);
          runScripts(replacement);
          setup(replacement);
        });

        history.replaceState(history.state, '', url);

        var newProduct = productContainer();
        var newTitle = newProduct && newProduct.getAttribute('data-product-title');
        if (pageTitle) {
          document.title = pageTitle;
        } else if (oldTitle && newTitle && document.title.indexOf(oldTitle) !== -1) {
          document.title = document.title.replace(oldTitle, newTitle);
        }

        if (theme.initGlobals) theme.initGlobals();
        if (theme.rteInit) theme.rteInit();
        if (window.AOS) AOS.refreshHard();
        if (newProduct) refreshShopperApproved(newProduct);

        if (restoreFocus && newProduct) {
          var active = newProduct.querySelector('a[data-sibling-swap][aria-current="true"]');
          if (active) active.focus({ preventScroll: true });
        }

        document.dispatchEvent(new CustomEvent('product:sibling-swapped', {
          detail: { url: url, productId: newProduct && newProduct.getAttribute('data-product-id') }
        }));
      })
      .catch(function (err) {
        if (token !== requestToken) return;
        console.warn('[product-sibling-swap] falling back to page load:', err);
        window.location.assign(url);
      });
  }

  function swapLink(target) {
    var link = target.closest && target.closest('a[data-sibling-swap]');
    if (!link || link.closest('[data-modal="true"]')) return null;
    return link;
  }

  document.addEventListener('click', function (e) {
    var link = swapLink(e.target);
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    e.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;
    swap(link.href, link);
  });

  // Warm the cache on intent so the swap feels instant
  function prefetch(e) {
    var link = swapLink(e.target);
    if (!link || link.getAttribute('aria-current') === 'true') return;
    var ids = sectionIds(templateSections());
    if (!ids.length || ids.length > MAX_SECTIONS) return;
    fetchSections(link.href, ids).catch(function () {});
  }

  document.addEventListener('mouseover', prefetch);
  document.addEventListener('focusin', prefetch);
  document.addEventListener('touchstart', prefetch, { passive: true });
})();
